import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'primary' | 'white';
  text?: string;
  fullScreen?: boolean;
}

const spinnerSizes = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12'
};

const spinnerVariants = {
  default: 'text-gray-600',
  primary: 'text-blue-600',
  white: 'text-white'
};

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'default',
  text,
  fullScreen = false
}) => {
  const spinner = (
    <div className="flex flex-col items-center justify-center space-y-3\" role="status">
      <Loader2
        className={cn(
          'animate-spin',
          spinnerSizes[size],
          spinnerVariants[variant]
        )}
        aria-hidden="true"
      />
      {text && (
        <p className={cn(
          'text-sm font-medium',
          variant === 'white' ? 'text-white' : 'text-gray-600'
        )} aria-live="polite">
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
        <LoadingSpinner size="lg" text="Vérification de l'authentification..." />
      </div>
    );
  }

  return spinner;
};

export const LoadingDots: React.FC<{
  variant?: 'default' | 'primary' | 'white';
}> = ({ variant = 'default' }) => {
  const dotColor = {
    default: 'bg-gray-600',
    primary: 'bg-blue-600',
    white: 'bg-white'
  };

  return (
    <div className="flex space-x-1" role="status">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            'w-2 h-2 rounded-full animate-pulse',
            dotColor[variant]
          )}
          style={{
            animationDelay: `${i * 0.2}s`,
            animationDuration: '1s'
          }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
};

export const LoadingBar: React.FC<{
  progress?: number;
  indeterminate?: boolean;
}> = ({ progress, indeterminate = false }) => {
  return (
    <div 
      className="w-full bg-gray-200 rounded-full h-1 overflow-hidden" 
      role="progressbar" 
      aria-valuemin={0} 
      aria-valuemax={100} 
      aria-valuenow={indeterminate ? undefined : progress || 0}
    >
      <div
        className={cn(
          'h-full bg-blue-600 transition-all duration-300',
          indeterminate && 'animate-pulse'
        )}
        style={{
          width: indeterminate ? '100%' : `${progress || 0}%`
        }}
      />
    </div>
  );
};

export default LoadingSpinner;